/**
 * This is an "Assign" button which will open the Create Assignment modal so
 * users can assign content.
 **/
import * as React from "react";
import {useDispatch, useSelector} from "react-redux";

import {useIsMounted, useOnMountEffect} from "@khanacademy/wonder-blocks-core";
import {ModalLauncher} from "@khanacademy/wonder-blocks-modal";
import {
    checkHasGroupedExercises,
    fetchClassLists,
    newAssignmentWithAssignees,
    setContentDescriptorsList,
    stopEditingAssignment,
    updateEditedAssignment,
} from "@khan/assignments-store";
import type {
    State as AssignmentStoreState,
    NewAssignmentSubmitParameters,
    QuestionSetTypeEnum,
    Student,
} from "@khan/assignments-types";
import {getUserHasChildAssignmentsClassroomStatus} from "@khan/assignments-utils";
import {useGql} from "@khan/data-gql";

import AssignmentModal from "./assignment-modal.tsx";

type AssignmentUpdateProps =
    | {
          dueDate: Date | null | undefined;
      }
    | {
          startDate: Date | null | undefined;
      }
    | {
          students: Array<Student>;
      }
    | {
          questionSetType: QuestionSetTypeEnum;
      };

interface Props {
    contentDescriptors: Array<string>;
    classroomDescriptor: string;
    studentKaids: Array<string>;
    onSuccess?: () => unknown;
    onClose?: () => unknown;
    onSubmit?: (params: NewAssignmentSubmitParameters) => unknown;
}

const CreateAssignmentModalLauncher = (props: Props): React.ReactNode => {
    const {
        contentDescriptors,
        classroomDescriptor,
        studentKaids,
        onSuccess,
        onClose,
        onSubmit,
    } = props;

    // Redux hooks
    const dispatch = useDispatch();
    const assignment = useSelector(
        (state: AssignmentStoreState) => state.editingAssignment.assignment,
    );
    const hasGroupedExercises = useSelector(
        (state: AssignmentStoreState) => state.hasGroupedExercises,
    );
    const studentLists = useSelector(
        (state: AssignmentStoreState) => state.studentLists,
    );

    const gqlFetch = useGql();

    const [modalIsOpen, setModalIsOpen] = React.useState(true);
    const isMounted = useIsMounted();

    // Action dispatchers
    const onAssignmentUpdate = React.useCallback(
        (updateProps: AssignmentUpdateProps) => {
            dispatch(updateEditedAssignment(updateProps));
        },
        [dispatch],
    );

    const initiateNewAssignment = React.useCallback((): void => {
        const newAssignmentWithAssigneesAction = newAssignmentWithAssignees(
            classroomDescriptor,
            studentKaids,
        );
        dispatch(newAssignmentWithAssigneesAction);
        const setContentDescriptorsListAction =
            setContentDescriptorsList(contentDescriptors);
        dispatch(setContentDescriptorsListAction);
    }, [dispatch, contentDescriptors, classroomDescriptor, studentKaids]);

    // Effect for componentDidMount equivalent - runs only once on mount
    useOnMountEffect(() => {
        // We need to fetch class lists here so that we have a list of all the
        // students in each class for assigning content
        fetchClassLists(gqlFetch)(dispatch);
    });

    // Effect for handling contentDescriptors changes (mount + updates)
    React.useEffect(() => {
        if (contentDescriptors && contentDescriptors.length > 0) {
            initiateNewAssignment();
            checkHasGroupedExercises(gqlFetch, contentDescriptors)(dispatch);
        }
    }, [contentDescriptors, initiateNewAssignment, gqlFetch, dispatch]);

    // Effect for componentWillUnmount equivalent
    React.useEffect(() => {
        return () => {
            if (isMounted()) {
                dispatch(stopEditingAssignment());
            }
        };
    }, [dispatch, isMounted]);

    if (!assignment) {
        return null;
    }

    const successModal = () => {
        if (onSuccess) {
            onSuccess();
        }
    };

    const closeModal = () => {
        setModalIsOpen(false);
        if (onClose) {
            onClose();
        }
    };

    const userHasCACs = getUserHasChildAssignmentsClassroomStatus(studentLists);

    return (
        <ModalLauncher
            opened={modalIsOpen}
            onClose={closeModal}
            modal={() => (
                <AssignmentModal
                    assignment={assignment}
                    qualifiedContentDescriptorsList={contentDescriptors}
                    // This is a dummy value that is not used by the createAssignments mutation because,
                    // in this case, Course IDs are included in the qualifiedContentDescriptorsList.
                    subjectSlug="FOOBIE:DOOBIEDOO"
                    onAssignmentUpdate={onAssignmentUpdate}
                    onClose={() => closeModal()}
                    onMutationSuccess={() => successModal()}
                    onMutationSubmit={onSubmit}
                    hasGroupedExercises={hasGroupedExercises}
                    hasChildAssignmentsClassroom={userHasCACs}
                />
            )}
        />
    );
};

export default CreateAssignmentModalLauncher;
